import React, { useEffect, useRef, useState } from 'react';
import { IHierarchy, IWeatherChartDataResModel, IWeatherStationModel } from '@/util/Request/requestType';
import './index.less';
import ReactEcharts from 'echarts-for-react';
import { Tabs } from 'antd';
import ColorMaker from '@/util/colorMaker';
import { useSelector } from 'react-redux';
import { RootState } from '@reduxjs/toolkit';
import { WeatherDownloadIcon, WeatherScientistIcon } from '@/resource/IconComponentSets';
import IF from '@/components/IF';


interface IProps {
    stationData: IWeatherStationModel[]
    weatherProperties?: IHierarchy[]
}

const mapObj = {
    E_AIR_TEMPERATURE: 'Temperature (°C)',
    E_ATM_PRESSURE: 'Pressure (hPa)',
    E_DEWPOINT: 'Dewpoint Temperature (°C)',
    E_ELEC_RESISTANCE: 'Electrical resistance (ohm)',
    E_LEAF_SENSOR_CONDUCTIVITY: 'Leaf Sensor Conductivity (mV)',
    E_LEAF_TEMPERATURE: 'Leaf Temperature (°C)',
    E_LEAF_WETNESS: 'Leaf wetness probability (%)',
    E_PAR_DN: 'Photosynthetic active radiation (W/m²)',
    E_PH: 'pH',
    E_RAINFALL: 'Precipitation Total (mm)',
    E_REF_ET: 'Evapotranspiration (mm)',
    E_RELATIVE_HUMIDITY: 'Relative Humidity (%)',
    E_SHRT_WAVE_RAD_DN: 'Shortwave Radiation (W/m²)',
    E_SNOW_DEPTH: 'Snow Depth (m)',
    E_SOIL_TEMPERATURE: 'Soil Temperature (°C)',
    E_SOIL_WATER_CONTENT: 'Soil Moisture (m³/m³)',
    E_SOIL_WATER_POTENTIAL: 'Soil Moisture Available To Plant (fraction)',
    E_SOIL_WATER_TENSION: 'Soil tension (kPa)',
    E_V_PRESS_DEFICIT: 'Vapor Pressure Deficit (hPa)',
    E_WATER_DEPTH: 'Water Depth (m)',
    E_WATER_TEMPERATURE: 'Water Temperature (°C)',
    E_WB_TEMPERATURE: 'Wetbulb Temperature (°C)',
    E_WIND_DIRECTION: 'Wind Direction (°)',
    E_WIND_GUST: 'Wind Gust (km/h)',
    E_WIND_HEADING: 'Wind Direction (°)',
    E_WIND_SPEED: 'Wind Speed (km/h)',
    M_WATER_PRESSURE: 'Water Pressure (hPa)',
    SOLAR_IRRADIATION: 'Shortwave Radiation (W/m²)',
    SUNSHINE_HOURS: 'Sunshine Duration (min)',
    VAPOR_PRESSURE: 'Vapor Pressure (hPa)',
    E_WATERMARK_30CM: 'Watermark 30cm (kPa)',
    E_WATERMARK_60CM: 'Watermark 60cm (kPa)',
    E_SOIL_MOISTURE_10CM: 'Soil Moisture (%)/10 cm',
    E_SOIL_MOISTURE_20CM: 'Soil Moisture (%)/20 cm',
    E_SOIL_MOISTURE_30CM: 'Soil Moisture (%)/30 cm',
    E_SOIL_MOISTURE_40CM: 'Soil Moisture (%)/40 cm',
    E_SOIL_MOISTURE_50CM: 'Soil Moisture (%)/50 cm',
    E_SOIL_MOISTURE_60CM: 'Soil Moisture (%)/60 cm',
    E_SOIL_MOISTURE_TENSION_L1: 'Soil Moisture Tension (kPa)/L1',
    E_SOIL_MOISTURE_TENSION_L2: 'Soil Moisture Tension (kPa)/L2',
    E_SOIL_MOISTURE_TENSION_L3: 'Soil Moisture Tension (kPa)/L3',
    E_SOIL_MOISTURE_TENSION_L4: 'Soil Moisture Tension (kPa)/L4',
    E_SOIL_WATER_VOLUMETRIC_CONTENT_10CM: 'Volumetric Ionic Content (VIC)/10 cm',
    E_SOIL_WATER_VOLUMETRIC_CONTENT_20CM: 'Volumetric Ionic Content (VIC)/20 cm',
    E_SOIL_WATER_VOLUMETRIC_CONTENT_30CM: 'Volumetric Ionic Content (VIC)/30 cm',
    E_SOIL_WATER_VOLUMETRIC_CONTENT_40CM: 'Volumetric Ionic Content (VIC)/40 cm',
    E_SOIL_WATER_VOLUMETRIC_CONTENT_50CM: 'Volumetric Ionic Content (VIC)/50 cm',
    E_SOIL_WATER_VOLUMETRIC_CONTENT_60CM: 'Volumetric Ionic Content (VIC)/60 cm',
    E_SOIL_TEMPERATURE_2CM: 'Soil Temperature (°C)/2 cm',
    E_SOIL_TEMPERATURE_5CM: 'Soil Temperature (°C)/5 cm',
    E_SOIL_TEMPERATURE_10CM: 'Soil Temperature (°C)/10 cm',
    E_SOIL_TEMPERATURE_20CM: 'Soil Temperature (°C)/20 cm',
    E_SOIL_TEMPERATURE_30CM: 'Soil Temperature (°C)/30 cm',
    E_SOIL_TEMPERATURE_40CM: 'Soil Temperature (°C)/40 cm',
    E_SOIL_TEMPERATURE_50CM: 'Soil Temperature (°C)/50 cm',
    E_SOIL_TEMPERATURE_60CM: 'Soil Temperature (°C)/60 cm',
    E_SOIL_TEMPERATURE_100CM: 'Soil Temperature (°C)/100 cm',
    E_AIR_TEMPERATURE_NODE1: 'Temperature (°C)/node1',
    E_CANOPY_TEMPERATURE: 'Canopy Temperature (°C)'
};


interface IChartOptionsProps {
    title: {
        text: string
        textStyle?: {
            color?: string
            fontWeight?: string
            fontSize?: number
        }
    }
    tooltip: {
        trigger: string
        axisPointer?: {
            type: string
            label: {
                backgroundColor: string
            }
        }
    }
    grid: {
        left: string
        right: string
        bottom: string
        top?: string
        containLabel: boolean
    }
    xAxis: {
        // value | category | time | log
        type: string
        boundaryGap: boolean
        data: string[]
        axisLabel?: any
    },
    yAxis: {
        type: string
    },
    animation: boolean
    series: IChartLineProps[]
}

interface IChartLineProps {
    name: string
    type: string
    // stack: string
    data: number[]
    celisusValues: number[]
    fahrenheitValues: number[]
    kmValues: number[]
    msValues: number[]
    stationId: string
    selected: boolean
    supplier: string
    longitude: string
    latitude: string
    color: string
    variableName: string
    isTemperature: boolean
    isWindSpeed: boolean,
    unit: string
    country?: string
    placeName?: string
    symbol?: string
}

interface IItems {
    label: string
    key: string
}

interface ITotalObj {
    [unknown: string]: {
        chartData?: IChartOptionsProps
        stationData?: IChartLineProps[]
        originData?: IWeatherChartDataResModel[]
        temperatureUnit: ITempratureUnit
        windSpeedUnit: IWindSpeedUnit
    }
}

enum ITempratureUnit {
    Celisus = 'C',
    Fahrenheit = 'F'
}

enum IWindSpeedUnit {
    KmH = 'Km/h',
    MS = 'm/s'
}

const SoilChartComponent = (props: IProps) => {
    const {
        weatherProperties,
        stationData
    } = props;

    const echartRef = useRef(null);

    const [render, setRender] = useState(false);
    const [weatherPropertiesTab, setWeatherPropertiesTab] = useState<string[]>([]);
    const [currenTab, setCurrenTab] = useState<string>();
    const [items, setItems] = useState<IItems[]>([]);
    const [totalObj, setTotalObj] = useState<ITotalObj>({});

    const temperature = useSelector((state: RootState) => state.weatherStation.isCelsius);
    const windSpeed = useSelector((state: RootState) => state.weatherStation.speed);
    const variableMapToUm = useSelector((state: RootState) => state.weatherStation.variableMapToUm);
    const chartData = useSelector((state: RootState) => state.weatherStation.chartData);

    // SECTION: fcuntion area =====================================================================================
    const selectAllButtonClicked = () => {
        const keysArr = Object.keys(totalObj);
        for (const itemKey of keysArr) {
            const stationData = [...totalObj[itemKey].stationData];
            stationData.forEach(station => station.selected = true);
            totalObj[itemKey].chartData.series = stationData;
            setTotalObj({ ...totalObj });
        }
    };

    const downloadCurrentChart = () => {
        const base64 = echartRef.current._api.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
        const blob = transformBase64ToBlob(base64);
        const link = document.createElement('a');
        const propertyArr = currenTab.split('-');

        link.download = `${propertyArr[0]} ${mapObj[propertyArr[1]]}.png`;
        link.style.display = 'none';
        link.href = URL.createObjectURL(blob);
        document.body.append(link);
        link.click();
        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
    };

    const transformBase64ToBlob = (base64: string) => {
        const picStr = base64.split(';base64,');
        const contentType = picStr[0].split(':')[1];
        const buffer = atob(picStr[1]);

        let bufferLength = buffer.length;
        const u8Arr = new Uint8Array(bufferLength);
        while (bufferLength--) {
            u8Arr[bufferLength] = buffer.charCodeAt(bufferLength);
        }
        const blob = new Blob([u8Arr], { type: contentType });
        return blob;
    };

    const transportToTargetFormat = (dateStr: string): string => {
        const monthMap = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'July', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const year = dateStr.substring(2, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        const hour = dateStr.match(/(?<=T)[\d]*/);
        let targetStr;
        if (hour && hour[0]) {
            targetStr = `${hour}:00 ${day} ${monthMap[parseInt(month, 10) - 1]} ${year}`;
        } else {
            targetStr = `${day} ${monthMap[parseInt(month, 10) - 1]} ${year}`;
        }

        return targetStr;
    };

    const unselectAllButtonClicked = () => {
        const keysArr = Object.keys(totalObj);
        const newLineCharts = totalObj[currenTab].stationData;

        for (const itemKey of keysArr) {
            totalObj[itemKey].chartData.series.forEach(stationData => {
                stationData.selected = false;
            });
        }
        const newLneChartsSeries = newLineCharts.filter((lineItem) => lineItem.selected);
        const newLineChartsOptions = {
            ...totalObj[currenTab].chartData,
            series: newLneChartsSeries
        };
        setTotalObj({ ...totalObj, [currenTab]: { ...totalObj[currenTab], chartData: newLineChartsOptions } });
    };

    const onStationClicked = (station: IChartLineProps) => {
        const newLineCharts = totalObj[currenTab].stationData;

        const indexRes = newLineCharts.findIndex((value) => {
            if (value.stationId === station.stationId) {
                return true;
            }
            return false;
        });
        newLineCharts[indexRes].selected = !newLineCharts[indexRes].selected;
        const newLneChartsSeries = newLineCharts.filter((lineItem) => lineItem.selected);
        const isTemperature = currenTab.includes('TEMPERATURE');
        const isWindSpeed = currenTab.includes('WIND_SPEED');

        for (const chartSerie of newLneChartsSeries) {
            if (isTemperature) {
                if (temperature) {
                    chartSerie.data = chartSerie.celisusValues;
                } else {
                    chartSerie.data = chartSerie.fahrenheitValues;
                }
            }
            if (isWindSpeed) {
                if (windSpeed) {
                    chartSerie.data = chartSerie.kmValues;
                } else {
                    chartSerie.data = chartSerie.msValues;
                }
            }
        }
        const newLineChartsOptions = {
            ...totalObj[currenTab].chartData,
            series: newLneChartsSeries
        };

        setTotalObj({ ...totalObj, [currenTab]: { ...totalObj[currenTab], chartData: newLineChartsOptions } });
    };

    const defineTheChartInstance = (param) => {
        echartRef.current = param;
    };

    // SECTION: useEffect area =====================================================================================

    useEffect(() => {
        const targetObj = totalObj[currenTab];

        if (!targetObj?.chartData?.series[0]?.isTemperature) {
            return;
        }

        if (temperature && targetObj?.temperatureUnit === 'F') {
            targetObj.temperatureUnit = ITempratureUnit.Celisus;
            const newSeries: IChartLineProps[] = [];
            for (const chartLineItem of targetObj.chartData.series) {
                chartLineItem.data = chartLineItem.celisusValues;
                newSeries.push(chartLineItem);
            }
            const newChartData: IChartOptionsProps = {
                ...targetObj.chartData,
                series: newSeries,
                title: {
                    text: '℃'
                }
            };
            targetObj.chartData = newChartData;
        }

        if (!temperature && targetObj?.temperatureUnit === 'C') {
            targetObj.temperatureUnit = ITempratureUnit.Fahrenheit;
            const newSeries: IChartLineProps[] = [];
            for (const chartLineItem of targetObj.chartData.series) {
                chartLineItem.data = chartLineItem.fahrenheitValues;
                newSeries.push(chartLineItem);
            }
            const newChartData: IChartOptionsProps = {
                ...targetObj.chartData,
                series: newSeries,
                title: {
                    text: '℉'
                }
            };
            targetObj.chartData = newChartData;
        }

        setTotalObj({ ...totalObj, [currenTab]: { ...targetObj } });
    }, [temperature]);

    useEffect(() => {
        const targetObj = totalObj[currenTab];

        if (!targetObj?.chartData?.series[0]?.isWindSpeed) {
            return;
        }

        if (windSpeed === 1 && targetObj?.windSpeedUnit === 'Km/h') {
            targetObj.windSpeedUnit = IWindSpeedUnit.MS;
            const newSeries: IChartLineProps[] = [];
            for (const chartLineItem of targetObj.chartData.series) {
                chartLineItem.data = chartLineItem.msValues;
                newSeries.push(chartLineItem);
            }
            targetObj.chartData = {
                ...targetObj.chartData,
                series: newSeries
            };
        }

        if (windSpeed === 0 && targetObj?.windSpeedUnit === 'm/s') {
            targetObj.windSpeedUnit = IWindSpeedUnit.KmH;
            const newSeries: IChartLineProps[] = [];
            for (const chartLineItem of targetObj.chartData.series) {
                chartLineItem.data = chartLineItem.kmValues;
                newSeries.push(chartLineItem);
            }
            targetObj.chartData = {
                ...targetObj.chartData,
                series: newSeries
            };
        }
        setTotalObj({ ...totalObj, [currenTab]: { ...targetObj } });
    }, [windSpeed]);

    useEffect(() => {
        if (totalObj[currenTab]) {
            setRender(false);
            setTimeout(() => setRender(true), 10);
        }
    }, [currenTab, totalObj]);

    useEffect(() => {
        if (weatherProperties?.length > 0) {
            let latestWeatherPropertiesTabStr = weatherProperties.map((weatherProperty) => {
                if (weatherProperties) {
                    return `${weatherProperty?.supplier}-${weatherProperty?.variable}-${weatherProperty?.frequency}`;
                }
            });
            const weatherPropertiesSet = new Set(latestWeatherPropertiesTabStr);
            latestWeatherPropertiesTabStr = Array.from(weatherPropertiesSet);
            setWeatherPropertiesTab(latestWeatherPropertiesTabStr);
            setCurrenTab(latestWeatherPropertiesTabStr[0]);
        }
    }, [weatherProperties]);

    useEffect(() => {
        const items = weatherPropertiesTab.map((weatherProperty) => {
            const nameArr = weatherProperty.split('-');
            return {
                label: `${nameArr[0]}-${mapObj[nameArr[1]]}-${nameArr[2]}`,
                key: weatherProperty,
            };
        });
        setItems(items);
    }, [weatherPropertiesTab]);

    useEffect(() => {
        if (variableMapToUm !== null) {
            const localTotalObj: ITotalObj = {};
            const colorMarker = new ColorMaker({ totalColorsNumber: 300 });
            let categorySetObj: {
                [keyText: string]: Set<string>
            };

            if (chartData) {
                chartData.forEach((chartDataItem, index) => {
                    const propertyStr = `${chartDataItem.supplier}-${chartDataItem.variable}-${chartDataItem.frequency}`;

                    if (!chartDataItem.hasData) {
                        return;
                    }

                    const color = colorMarker.getColor(index);
                    // category just collect from the first data

                    if (chartDataItem.hasData && chartDataItem.timeSequence.length > 0) {
                        chartDataItem.timeSequence.forEach((time: string) => {
                            if (categorySetObj && categorySetObj[propertyStr]) {
                                categorySetObj[propertyStr].add(time);
                            } else {
                                const timeSet = new Set<string>();
                                timeSet.add(time);
                                if (categorySetObj) {
                                    categorySetObj[propertyStr] = timeSet;
                                } else {
                                    categorySetObj = {
                                        [propertyStr]: timeSet,
                                    };
                                }
                            }
                        });
                    }
                    const valueArr = chartDataItem.values.map((value) => parseFloat(value));
                    const isTemperature = chartDataItem.variable.includes('TEMPERATURE') || chartDataItem.variable.includes('E_DEWPOINT');
                    const isWindSpeed = chartDataItem.variable.includes('WIND_SPEED');
                    let fahrenheitArr;
                    if (isTemperature) {
                        fahrenheitArr = valueArr.map((value) => value * 1.8 + 32.0);
                    }
                    let msValueArr;
                    if (isWindSpeed) {
                        msValueArr = valueArr.map((value) => (value * 1000) / 3600);
                    }
                    const targetStationArr = stationData.filter((station) => station.stationID === chartDataItem.stationID);
                    let targetStation: IWeatherStationModel;
                    if (targetStationArr.length > 0) {
                        targetStation = targetStationArr[0];
                    }
                    const data: IChartLineProps = {
                        name: `${chartDataItem.supplier}-${chartDataItem.stationID}`,
                        type: 'line',
                        // stack: 'Total',
                        data: valueArr,
                        celisusValues: [...valueArr],
                        fahrenheitValues: fahrenheitArr,
                        kmValues: valueArr,
                        msValues: msValueArr,
                        stationId: chartDataItem.stationID,
                        selected: false,
                        supplier: chartDataItem.supplier,
                        longitude: chartDataItem.longitude,
                        latitude: chartDataItem.latitude,
                        color,
                        variableName: mapObj[chartDataItem.variable] || 'Unknow',
                        isTemperature,
                        isWindSpeed,
                        unit: chartDataItem.uom,
                        country: targetStation && targetStation.country,
                        placeName: targetStation && targetStation.placeName,
                    };
                    // define the initial unit
                    if (localTotalObj[propertyStr] && localTotalObj[propertyStr]['stationData']) {
                        localTotalObj[propertyStr]['stationData'].push(data);
                    } else {
                        localTotalObj[propertyStr] = {
                            ...localTotalObj[propertyStr],
                            stationData: [data],
                            temperatureUnit: ITempratureUnit.Celisus,
                            windSpeedUnit: IWindSpeedUnit.KmH
                        };
                    }

                    if (localTotalObj[propertyStr] && localTotalObj[propertyStr]['orginData']) {
                        localTotalObj[propertyStr]['orginData'].push(chartDataItem);
                    } else {
                        localTotalObj[propertyStr] = { ...localTotalObj[propertyStr], originData: [chartDataItem] };
                    }
                });

                const keys = Object.keys(localTotalObj);
                for (const key of keys) {
                    const data = localTotalObj[key].stationData;
                    let title;
                    const targetData = data.filter((dataItem, index) => {
                        if (!title) {
                            title = dataItem.unit;
                        }
                        if (index < 3) {
                            dataItem.selected = true;
                            return true;
                        }
                    });
                    const categoryArr: string[] = [...categorySetObj[key]];
                    const transportDateArr = categoryArr.map((date) => transportToTargetFormat(date));
                    const constructChartOptionData: IChartOptionsProps = {
                        title: {
                            text: title
                        },
                        tooltip: {
                            trigger: 'item',
                        },
                        grid: {
                            left: '3%',
                            right: '8%',
                            bottom: '2%',
                            containLabel: true
                        },
                        xAxis: {
                            // value | category | time | log
                            type: 'category',
                            boundaryGap: false,
                            data: transportDateArr,
                            axisLabel: {
                                formatter: function (name) {
                                    if (!name.includes(':')) {
                                        return name;
                                    } else {
                                        return name.match(/(?<=\d\d:\d\d )[\w ]*/)[0];
                                    }
                                },
                                rotate: -40
                            }
                        },
                        yAxis: {
                            type: 'value'
                        },
                        series: targetData,
                        animation: false
                    };
                    localTotalObj[key]['chartData'] = constructChartOptionData;
                }
            }
            console.log('localTotalObj:===', localTotalObj);
            // console.log('setCurrenTab:===', setCurrenTab);
            // console.log('totalObj[currenTab].chartData:====', localTotalObj[currenTab].chartData);
            setTotalObj(localTotalObj);
        }

    }, [chartData]);

    const constructNullChartOptionData: IChartOptionsProps = {
        title: {
            text: ''
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: '#6a7985'
                }
            }
        },
        grid: {
            left: '3%',
            right: '8%',
            bottom: '2%',
            containLabel: true
        },
        xAxis: {
            // value | category | time | log
            type: 'category',
            boundaryGap: false,
            data: [],
            axisLabel: {
                formatter: function (name) {
                    if (!name.includes(':')) {
                        return name;
                    } else {
                        return name.match(/(?<=\d\d:\d\d )[\w ]*/)[0];
                    }
                },
                rotate: -40
            },
        },
        yAxis: {
            type: 'value'
        },
        series: [],
        animation: false
    };

    return (
        <div className='chart-container'>
            <Tabs style={{ height: '50px' }} items={items} onChange={(localCurrenTab) => {
                setCurrenTab(localCurrenTab);
            }} />
            <IF show={Boolean(totalObj[currenTab] && totalObj[currenTab].chartData)}>
                <div className='chart-div'>
                    {
                        render && (
                            <ReactEcharts
                                option={totalObj[currenTab] && totalObj[currenTab].chartData ? totalObj[currenTab].chartData : constructNullChartOptionData}
                                style={{ width: 'auto', height: '95%' }}
                                lazyUpdate={true}
                                onChartReady={(chartInstance) => defineTheChartInstance(chartInstance)}
                                notMerge={true}
                            // opts={{ renderer: 'canvas' }}
                            />
                        )
                    }
                    <button className='chart-download-button' onClick={() => downloadCurrentChart()}><WeatherDownloadIcon />Download chart</button>
                </div>
                <div className='weather-select-div'>
                    <button onClick={() => selectAllButtonClicked()}>
                        Select all
                    </button>
                    <button onClick={() => unselectAllButtonClicked()}>
                        Unselect all
                    </button>
                </div>
                <div className='weather-bottom-menu'>
                    {
                        totalObj[currenTab] && totalObj[currenTab].stationData?.map((station) => {
                            const {
                                stationId,
                                longitude,
                                latitude,
                                selected,
                                country,
                                placeName
                            } = station;
                            return (
                                <div
                                    className='station-div'
                                    key={stationId}
                                    onClick={() => onStationClicked(station)}
                                >
                                    <div className='station-info-div'>
                                        <div className='title-div'>
                                            <span className='station-id-span'>{stationId}  <span className='station-info-span'>{`(${parseFloat(longitude).toFixed(4)} ${parseFloat(latitude).toFixed(4)})`}</span></span>
                                            <div style={{ backgroundColor: `${selected ? station.color : 'transparent'}`, borderColor: `${station.selected && station.color}` }} className='point-div' />
                                        </div>
                                        <div className='area-div'>
                                            <span className='station-country'>{country}</span>
                                            <span className='station-place-name'>{placeName}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>
            </IF>
            <IF show={!(totalObj[currenTab] && totalObj[currenTab].chartData)}>
                <div className='weather-empty-div'>
                    <span className='no-data-span'>No data</span>
                    <WeatherScientistIcon />
                </div>
            </IF>
        </div>
    );
};

export type {
    IChartLineProps,
    IChartOptionsProps
};

export default SoilChartComponent;